import React from "react";
import { makeClassName, Row } from "@hex-insights/core";
import { FormValues, TextField, TextFieldInputProps, TextFieldProps, TextInput } from "@hex-insights/forms";
import { isTouchScreen } from "../../../Utilities";
import { Icon } from "../../Icon";
import styles from "./styles.module.css";

export type SearchFieldProps<FV extends FormValues> = Omit<TextFieldProps<FV>, "Input"> & {
	fieldClassName?: string;
	fieldStyle?: React.CSSProperties;
	onFocus?: () => void;
	onBlur?: () => void;
};

// TODO hex-core update support onFocus and onBlur in fields

export function SearchField<FV extends FormValues>({
	className,
	style,
	fieldClassName,
	fieldStyle,
	noHeader = true,
	placeholder = "Search",
	onFocus,
	onBlur,
	autoFocus,
	...props
}: SearchFieldProps<FV>) {
	const Input = React.useCallback(
		({ onFocus: onFocusProp, onBlur: onBlurProp, ...props }: TextFieldInputProps) => {
			const fullOnFocus = React.useCallback(() => {
				onFocusProp?.();
				onFocus?.();
			}, [onFocusProp]);

			const fullOnBlur = React.useCallback(() => {
				onBlurProp?.();
				onBlur?.();
			}, [onBlurProp]);

			return <TextInput onFocus={fullOnFocus} onBlur={fullOnBlur} {...props} />;
		},
		[onFocus, onBlur],
	);

	return (
		<Row
			justify="spaced-start"
			align="center"
			horizontalSpacing="0.5rem"
			className={makeClassName(styles["search-field__container"], className)}
			style={style}
		>
			<span className={styles["search-field__icon__container"]}>
				<Icon.MagnifyingGlass size={16} className="block" />
			</span>
			<TextField
				{...props}
				Input={Input}
				noHeader={noHeader}
				placeholder={placeholder}
				autoFocus={isTouchScreen() ? false : autoFocus}
				className={makeClassName(styles["search-field__field"], fieldClassName)}
				style={fieldStyle}
			/>
		</Row>
	);
}
